import React, { useEffect, useId, useRef, useState } from 'react'
import { Html5Qrcode } from 'html5-qrcode'
import { Camera, X, Loader2 } from 'lucide-react'
import './MobileBarcodeScanner.css'

interface MobileBarcodeScannerProps {
  isOpen: boolean
  onClose: () => void
  onScan: (value: string) => void
  title?: string
}

function MobileBarcodeScanner({
  isOpen,
  onClose,
  onScan,
  title = 'Escanear código de barras'
}: MobileBarcodeScannerProps) {
  const reactId = useId()
  const scannerId = `barcode-scanner-${reactId.replace(/:/g, '')}`
  const scannerRef = useRef<Html5Qrcode | null>(null)
  const onScanRef = useRef(onScan)
  const onCloseRef = useRef(onClose)
  const [error, setError] = useState<string | null>(null)
  const [starting, setStarting] = useState(false)

  onScanRef.current = onScan
  onCloseRef.current = onClose

  useEffect(() => {
    if (!isOpen) return undefined

    let cancelled = false

    const startScanner = async () => {
      setStarting(true)
      setError(null)

      try {
        const scanner = new Html5Qrcode(scannerId)
        scannerRef.current = scanner

        const cameras = await Html5Qrcode.getCameras()
        if (!cameras.length) {
          throw new Error('Nenhuma câmera encontrada neste dispositivo.')
        }

        const rearCamera =
          cameras.find((camera) => /back|rear|traseira|environment/i.test(camera.label)) ||
          cameras[cameras.length - 1]

        if (cancelled) return

        await scanner.start(
          rearCamera.id,
          {
            fps: 10,
            qrbox: { width: 260, height: 160 },
            aspectRatio: 1.777778
          },
          (decodedText) => {
            const value = decodedText.trim()
            if (!value) return
            onScanRef.current(value)
            onCloseRef.current()
          },
          () => {}
        )
      } catch (scanError: any) {
        if (!cancelled) {
          setError(scanError?.message || 'Não foi possível acessar a câmera.')
        }
      } finally {
        if (!cancelled) {
          setStarting(false)
        }
      }
    }

    startScanner()

    return () => {
      cancelled = true
      const scanner = scannerRef.current
      scannerRef.current = null

      if (!scanner) return

      scanner
        .stop()
        .then(() => scanner.clear())
        .catch(() => scanner.clear())
    }
  }, [isOpen, scannerId])

  if (!isOpen) return null

  return (
    <div className="mobile-barcode-scanner-overlay" role="dialog" aria-modal="true">
      <div className="mobile-barcode-scanner-backdrop" onClick={onClose} />
      <div className="mobile-barcode-scanner-panel">
        <div className="mobile-barcode-scanner-header">
          <div className="mobile-barcode-scanner-title-wrap">
            <Camera size={18} />
            <h3>{title}</h3>
          </div>
          <button
            type="button"
            className="mobile-barcode-scanner-close"
            onClick={onClose}
            aria-label="Fechar câmera"
          >
            <X size={20} />
          </button>
        </div>

        <div className="mobile-barcode-scanner-body">
          {starting && (
            <div className="mobile-barcode-scanner-loading">
              <Loader2 size={24} className="mobile-barcode-scanner-spinner" />
              <span>Iniciando câmera...</span>
            </div>
          )}

          {error ? (
            <div className="mobile-barcode-scanner-error">
              <p>{error}</p>
              <p>Verifique as permissões da câmera no navegador.</p>
            </div>
          ) : (
            <div id={scannerId} className="mobile-barcode-scanner-viewport" />
          )}
        </div>

        <p className="mobile-barcode-scanner-hint">
          Aponte para o código de barras ou QR do produto.
        </p>
      </div>
    </div>
  )
}

export default MobileBarcodeScanner
